import { useEffect, useState, useCallback } from "react";
import { useAuth, SignIn, SignUp } from "@clerk/clerk-react";
import { Routes, Route, Navigate } from "react-router";
import { ViewportCtxProvider } from "@/contexts/ViewportCtx";
import { DocumentCtxProvider } from "@/contexts/DocumentCtx";
import { WorkspaceCtxProvider } from "@/contexts/WorkspaceCtx";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import McpAccess from "@/pages/McpAccess";
import StorageUsage from "@/pages/StorageUsage";
import Administration from "@/pages/Administration";
import McpAuthorize from "@/pages/McpAuthorize";
import loadingMarkUrl from "@/assets/brand/loading-screen-mark.svg";
import { useCanvasStore, SHAPE_PREFIX } from "@/store/useCanvasStore";

// Hard ceiling on the splash: if project loading hangs (e.g. a stalled auth
// token request on mobile), fall through to the app rather than trapping the
// user on the logo screen forever.
const SPLASH_MAX_MS = 10_000;

function BrandSplash() {
  return (
    <>
      <img src={loadingMarkUrl} width={40} height={40} alt="Taitora" draggable={false} />
      <span style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 700, fontSize: 18, color: "#F6F2EA", letterSpacing: "-0.02em" }}>
        taitora<span style={{ opacity: 0.6 }}>.ai</span>
      </span>
    </>
  );
}

// Clerk's <SignIn>/<SignUp> in "path" routing mode own their sub-routes
// (factor-one, SSO callback, …), so the parent <Route> must be a splat.
function AuthPage({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col items-center justify-center w-screen min-h-screen bg-[#111318]">{children}</div>;
}

// The signed-in application: canvas providers, shell, splash overlay and the
// canvas keyboard shortcuts. These only matter once a user is authenticated,
// so they live here rather than at the top level.
function AuthedApp() {
  const [dataReady, setDataReady] = useState(false);
  const [splashVisible, setSplashVisible] = useState(true);
  const handleProjectsLoaded = useCallback(() => setDataReady(true), []);

  // Fallback so a hung data load never leaves the user stuck on the splash.
  useEffect(() => {
    const t = setTimeout(() => setDataReady(true), SPLASH_MAX_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        useCanvasStore.getState().undo();
        return;
      }
      if (mod && (e.key === "z" && e.shiftKey || e.key === "y")) {
        e.preventDefault();
        useCanvasStore.getState().redo();
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        const { rfNodes, rfEdges, rfShapes } = useCanvasStore.getState();
        const nodeIds  = rfNodes.filter((n) => n.selected).map((n) => n.id);
        const edgeIds  = rfEdges.filter((e) => e.selected).map((e) => e.id);
        const shapeIds = rfShapes.filter((n) => n.selected).map((n) => n.id.slice(SHAPE_PREFIX.length));
        useCanvasStore.getState().deleteElements({ nodeIds, edgeIds, shapeIds });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (dataReady) {
      const t = setTimeout(() => setSplashVisible(false), 400);
      return () => clearTimeout(t);
    }
  }, [dataReady]);

  return (
    <ViewportCtxProvider>
      <DocumentCtxProvider>
        <WorkspaceCtxProvider>
          <AppShell onProjectsLoaded={handleProjectsLoaded} />
          {splashVisible && (
            <div
              className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-[#111318] transition-opacity duration-300 ${dataReady ? "opacity-0 pointer-events-none" : "opacity-100"}`}
            >
              <BrandSplash />
            </div>
          )}
        </WorkspaceCtxProvider>
      </DocumentCtxProvider>
    </ViewportCtxProvider>
  );
}

export default function App() {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 w-screen h-screen bg-[#111318]">
        <BrandSplash />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login/*"
        element={
          isSignedIn ? (
            <Navigate to="/" replace />
          ) : (
            <AuthPage>
              <SignIn routing="path" path="/login" signUpUrl="/signup" />
            </AuthPage>
          )
        }
      />
      <Route
        path="/signup/*"
        element={
          isSignedIn ? (
            <Navigate to="/" replace />
          ) : (
            <AuthPage>
              <SignUp routing="path" path="/signup" signInUrl="/login" />
            </AuthPage>
          )
        }
      />
      {/*
        Must sit before the splat, which would otherwise swallow it. Rendered
        outside AuthedApp on purpose: the page needs no canvas state, so it
        skips the brand splash, WS connect, and 2s DB sync entirely.
      */}
      <Route
        path="/connect/mcp"
        element={<McpAuthorize />}
      />
      <Route
        path="/settings/mcp"
        element={isSignedIn ? <McpAccess /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/settings/storage"
        element={isSignedIn ? <StorageUsage /> : <Navigate to="/login" replace />}
      />
      {/* Admin-gated on the backend (404 for non-admins); the page mirrors that. */}
      <Route
        path="/settings/admin"
        element={isSignedIn ? <Administration /> : <Navigate to="/login" replace />}
      />
      <Route path="*" element={isSignedIn ? <AuthedApp /> : <Landing />} />
    </Routes>
  );
}
